/*  ========================================================================
    # Sections - Set Card Slider
    ========================================================================  */

import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useLocalization from '../../hooks/useLocalization';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import tailwindConfig from '../../../../tailwind.config';
import bem from '../../helpers/bem';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';

const SetCardSlider = ({
  heading,
  entries
}) => {
  const { lgUp, } = useBreakpoints();
  const [activeSlide, setActiveSlide] = useState(0);
  const __ = useTranslation();

  // Icons
  const localize = useLocalization();
  const iconArrowLeft = `${localize('svgSprite')}#arrow-left`;
  const iconArrowRight = `${localize('svgSprite')}#arrow-right`;

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (entries.length > 1 && entries.length < 5) {
      tempEntries = [...entries];
      while (tempEntries.length < 5) tempEntries.push(...entries);
    } else {
      tempEntries = entries;
    }

    return tempEntries;
  }, [entries]);

  const shuffledEntries = useMemo(() => {
    return [...sliderEntries].sort(() => 0.5 - Math.random());
  }, [entries]);

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(shuffledEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < shuffledEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedRight: navigateLeft,
    onSwipedLeft: navigateRight
  });

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === shuffledEntries.length - 1) return -1;
    if (activeSlide === 0 && index === shuffledEntries.length - 2) return -2;
    if (activeSlide === 1 && index === shuffledEntries.length - 1) return -2;

    if (activeSlide === shuffledEntries.length - 1 && index === 0) return 1;
    if (activeSlide === shuffledEntries.length - 1 && index === 1) return 2;

    if (activeSlide === shuffledEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  return (
    <div className="container">
      {heading && <div className={bem('s-set-card-slider', 'header')} dangerouslySetInnerHTML={{ __html: heading }}>
      </div>}
          <div className={bem('s-set-card-slider', 'wrapper')} {...swipeHandlers}>
            <button
              className={bem('c-button', '', { black: true, icon: true, left: true })}
              title={__('section-set-card-slider-navigate-left')}
              aria-label={__('section-set-card-slider-navigate-left')}
              onClick={navigateLeft}
            >
              <svg viewBox='0 0 200 200'>
                <use href={iconArrowLeft}></use>
              </svg>
            </button>
            {shuffledEntries.map((entry, index) => (
              <div key={index} className={bem('s-set-card-slider', 'slide', { active: index == activeSlide })}
                style={shuffledEntries.length === 1 ? { opacity: 1 } : {}}
                data-offset={shuffledEntries.length === 1 ? '' : getSlideOffset(index)}
              >
                {lgUp ? <LazyImage
                  figureClass={bem('s-set-card-slider', 'slide-image')}
                  defaultUrl={entry.image_desktop.sizes['2048x2048']}
                  placeholderUrl={entry.image_desktop.sizes.placeholder}
                  alt={entry.image_desktop.alt}
                >
                  <LazySource
                    srcSet={entry.image_desktop.sizes['2048x2048']}
                    mediaMin={tailwindConfig.theme.screens.xxl}
                  />
                </LazyImage>
                  : <LazyImage
                    figureClass={bem('s-set-card-slider', 'slide-image')}
                    defaultUrl={entry.image_mobile.sizes.large}
                    placeholderUrl={entry.image_desktop.sizes.placeholder}
                    alt={entry.image_desktop.alt}
                  >
                    <LazySource
                      srcSet={entry.image_mobile.sizes.large}
                      mediaMin={tailwindConfig.theme.screens.sm}
                    />
                  </LazyImage>
                }
              </div>
            ))}
            <button
              className={bem('c-button', '', { black: true, icon: true, right: true })}
              title={__('section-set-card-slider-navigate-right')}
              aria-label={__('section-set-card-slider-navigate-right')}
              onClick={navigateRight}
            >
              <svg viewBox='0 0 200 200'>
                <use href={iconArrowRight}></use>
              </svg>
            </button>
          </div>
    </div>
  );
};

SetCardSlider.propTypes = {
  heading: PropTypes.string,
  entries: PropTypes.array
};

export default SetCardSlider;
